import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

/** Map integration (e.g. Procore) project status strings to Vermillion project.status values. */
function mapExternalProjectStatus(raw) {
  if (raw == null || raw === '') return 'active';
  const s = String(raw).toLowerCase().trim();
  if (['active', 'in progress', 'open', 'on track', 'preconstruction', 'bidding'].includes(s)) return 'active';
  if (['completed', 'complete', 'closed'].includes(s)) return 'completed';
  if (['on hold', 'on-hold', 'hold', 'suspended'].includes(s)) return 'on-hold';
  if (['cancelled', 'canceled', 'void', 'terminated'].includes(s)) return 'cancelled';
  return 'active';
}

/** Project number from integration payloads; Procore uses `project_number` (not `number`). */
function getExternalProjectNumber(project) {
  const raw = project?.project_number ?? project?.number ?? project?.code;
  if (raw == null) return '';
  return String(raw).trim();
}

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [statusFilterDropdownOpen, setStatusFilterDropdownOpen] = useState(false);
  const [dateFilterDropdownOpen, setDateFilterDropdownOpen] = useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showRetrieveModal, setShowRetrieveModal] = useState(false);
  const [retrieveIntegration, setRetrieveIntegration] = useState(null);
  const [retrieveIntegrationVuid, setRetrieveIntegrationVuid] = useState(null);
  const [retrievedProjectsData, setRetrievedProjectsData] = useState([]);
  const [showRetrievedProjectsModal, setShowRetrievedProjectsModal] = useState(false);
  const [selectedProjectsToImport, setSelectedProjectsToImport] = useState([]);
  const [externalIdsMap, setExternalIdsMap] = useState({}); // Map of project_vuid -> array of external IDs
  const [formData, setFormData] = useState({
    project_number: '',
    project_name: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'active',
    // Project Settings
    allow_project_cost_codes: false,
    allocate_contract_lines_to_cost_codes: false,
    labor_cost_method: 'actuals'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage] = useState(10);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Add click outside handler for status dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStatusDropdown && !event.target.closest('.status-dropdown-container')) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  // Add click outside handler for filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown-container')) {
        setStatusFilterDropdownOpen(false);
        setDateFilterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchIntegrations();
    fetchExternalIds();
  }, []);

  const fetchExternalIds = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/external-system-ids`, {
        params: {
          object_type: 'project'
        }
      });
      
      if (response.data) {
        // Group external IDs by project_vuid
        const idsMap = {};
        response.data.forEach(mapping => {
          if (mapping.project_vuid) {
            if (!idsMap[mapping.project_vuid]) {
              idsMap[mapping.project_vuid] = [];
            }
            idsMap[mapping.project_vuid].push({
              external_id: mapping.external_id,
              integration_name: mapping.integration?.integration_name || 'Unknown',
              integration_type: mapping.integration?.integration_type || 'unknown',
              external_status: mapping.external_status
            });
          }
        });
        setExternalIdsMap(idsMap);
      }
    } catch (error) {
      console.error('Error fetching external IDs:', error);
    }
  };

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations`);
      if (response.data) {
        // Filter for integrations that support projects
        const filteredIntegrations = response.data.filter(integration => 
          integration.enabled_objects && integration.enabled_objects.projects === true
        );
        setIntegrations(filteredIntegrations);
      }
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  // Handle navigation state for editing projects
  useEffect(() => {
    const handleLocationState = () => {
      if (window.location.state && window.location.state.editProject) {
        handleEdit(window.location.state.editProject);
        // Clear the state
        window.history.replaceState({}, document.title);
      }
    };

    handleLocationState();
  }, []);

  // Filter and paginate projects whenever projects or filters change
  useEffect(() => {
    filterAndPaginateProjects();
  }, [projects, searchTerm, statusFilter, dateFilter, currentPage, sortConfig]);

  const filterAndPaginateProjects = () => {
    // Ensure projects is always an array
    if (!Array.isArray(projects)) {
      setFilteredProjects([]);
      return;
    }

    let filtered = projects;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.project_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      const ninetyDaysAgo = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));

      filtered = filtered.filter(project => {
        const createdDate = new Date(project.created_at);
        if (dateFilter === 'recent') {
          return createdDate >= thirtyDaysAgo;
        } else if (dateFilter === 'older') {
          return createdDate < ninetyDaysAgo;
        }
        return true;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';
        
        // Handle numeric values
        if (sortConfig.key === 'total_contract_value' || sortConfig.key === 'current_budget') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }
        
        // Handle date values
        if (sortConfig.key === 'start_date' || sortConfig.key === 'end_date') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        }
        
        // Handle string values (project_name, project_number)
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return + 0;
      });
    }

    setFilteredProjects(filtered);
  };

  const getCurrentProjects = () => {
    const startIndex = (currentPage - 1) * projectsPerPage;
    const endIndex = startIndex + projectsPerPage;
    return filteredProjects.slice(startIndex, endIndex);
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'search') {
      setSearchTerm(value);
    } else if (filterType === 'status') {
      setStatusFilter(value);
    } else if (filterType === 'date') {
      setDateFilter(value);
    }
    setCurrentPage(1);
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/projects`);
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      if (error.response?.status === 401) {
        console.log('Authentication required - token may be missing or expired');
      }
      setProjects([]); // Set empty array on error to prevent undefined issues
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    const newErrors = {};
    if (!formData.project_number) newErrors.project_number = 'Project number is required';
    if (!formData.project_name) newErrors.project_name = 'Project name is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (editingProject) {
        // Update existing project
        await axios.put(`${API_BASE_URL}/api/projects/${editingProject.vuid}`, formData);
        
        // Update project settings
        const settingsData = {
          allow_project_cost_codes: formData.allow_project_cost_codes,
          allocate_contract_lines_to_cost_codes: formData.allocate_contract_lines_to_cost_codes,
          labor_cost_method: formData.labor_cost_method
        };
        
        try {
          await axios.put(`${API_BASE_URL}/api/projects/${editingProject.vuid}/settings`, settingsData);
          console.log('Project settings updated successfully');
        } catch (settingsError) {
          console.error('Error updating project settings:', settingsError);
          // Don't fail the entire operation if settings fail
        }
        
        setSuccessMessage('Project updated successfully!');
      } else {
        // Create new project
        const response = await axios.post(`${API_BASE_URL}/api/projects`, formData);
        const newProject = response.data;
        
        // Save project settings if this is a new project
        if (newProject && newProject.vuid) {
          const settingsData = {
            allow_project_cost_codes: formData.allow_project_cost_codes,
            allocate_contract_lines_to_cost_codes: formData.allocate_contract_lines_to_cost_codes,
            labor_cost_method: formData.labor_cost_method
          };
          
          try {
            await axios.put(`${API_BASE_URL}/api/projects/${newProject.vuid}/settings`, settingsData);
            console.log('Project settings saved successfully');
          } catch (settingsError) {
            console.error('Error saving project settings:', settingsError);
            // Don't fail the entire operation if settings fail
          }
        }
        
        setSuccessMessage('Project created successfully!');
      }
      
      // Reset form and refresh data
      setFormData({
        project_number: '',
        project_name: '',
        description: '',
        start_date: '',
        end_date: '',
        status: 'active',
        // Project Settings
        allow_project_cost_codes: false,
        allocate_contract_lines_to_cost_codes: false,
        labor_cost_method: 'actuals'
      });
      setEditingProject(null);
      setShowForm(false);
      setErrors({});
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      const msg = error.response?.data?.error || error.message || 'Error saving project. Please try again.';
      setErrors({ submit: typeof msg === 'string' ? msg : 'Error saving project. Please try again.' });
    }
  };

  const handleEdit = async (project) => {
    setEditingProject(project);
    setFormData({
      project_number: project.project_number || '',
      project_name: project.project_name || '',
      description: project.description || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      status: project.status || 'active',
      // Project Settings - will be loaded from API
      allow_project_cost_codes: false,
      allocate_contract_lines_to_cost_codes: false,
      labor_cost_method: 'actuals'
    });
    setShowForm(true);
    setErrors({});

    // Load project settings
    try {
      const response = await axios.get(`${API_BASE_URL}/api/projects/${project.vuid}/settings`);
      const settings = response.data;
      setFormData(prev => ({
        ...prev,
        allow_project_cost_codes: settings.allow_project_cost_codes || false,
        allocate_contract_lines_to_cost_codes: settings.allocate_contract_lines_to_cost_codes || false,
        labor_cost_method: settings.labor_cost_method || 'actuals'
      }));
    } catch (error) {
      console.error('Error loading project settings:', error);
      // Keep default values if settings can't be loaded
    }
  };

  const handleDelete = async (projectVuid) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/projects/${projectVuid}`);
        setSuccessMessage('Project deleted successfully!');
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        setErrors({ submit: 'Error deleting project. Please try again.' });
      }
    }
  };

  const handleIntegrationModal = (project) => {
    setSelectedProject(project);
    setShowIntegrationModal(true);
    fetchIntegrations();
  };

  const handleSendProjectToIntegration = async () => {
    if (!selectedIntegration) {
      alert('Please select an integration');
      return;
    }

    if (!selectedProject) {
      alert('No project selected');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/integrations/${selectedIntegration.vuid}/send-project`,
        { project_vuid: selectedProject.vuid }
      );

      if (response.data.success) {
        alert(`Project "${selectedProject.project_name}" sent to ${selectedIntegration.integration_name} successfully!`);
        setShowIntegrationModal(false);
        setSelectedProject(null);
        setSelectedIntegration(null);
        // Refresh external IDs to show the new mapping
        fetchExternalIds();
      } else {
        alert(`Error: ${response.data.error || 'Failed to send project'}`);
      }
    } catch (error) {
      console.error('Error sending project to integration:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to send project to integration';
      alert(`Error: ${errorMsg}`);
    }
  };

  const fetchProjectsFromIntegration = async (integrationVuid) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/integrations/${integrationVuid}/retrieve-projects`);
      
      if (response.data.success) {
        setRetrievedProjectsData(response.data.projects || []);
        setRetrieveIntegrationVuid(integrationVuid); // Store integration VUID for later use
        setRetrieveIntegration(response.data.integration_name || 'Procore');
      } else {
        console.error('Error fetching projects:', response.data.error);
        alert(`Error: ${response.data.error || 'Failed to retrieve projects'}`);
        setRetrievedProjectsData([]);
      }
    } catch (error) {
      console.error('Error fetching projects from integration:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to retrieve projects';
      alert(`Error: ${errorMsg}`);
      setRetrievedProjectsData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const [successMessage, setSuccessMessage] = useState('');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-2xl p-8 mb-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-4xl font-bold text-gray-900">Projects</h1>
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setShowRetrieveModal(true);
                }}
                className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors"
                data-tooltip="Retrieve projects from an integration"
              >
                <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Retrieve Projects</span>
              </button>
              <button
                onClick={() => {
                  setShowForm(true);
                  setEditingProject(null);
                  setFormData({
                    project_number: '',
                    project_name: '',
                    description: '',
                    start_date: '',
                    end_date: '',
                    status: 'active',
                    // Project Settings
                    allow_project_cost_codes: false,
                    allocate_contract_lines_to_cost_codes: false,
                    labor_cost_method: 'actuals'
                  });
                  setErrors({});
                  // Scroll to top when opening the create form
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add Project</span>
              </button>
            </div>
          </div>
          
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-600 font-medium">{successMessage}</p>
            </div>
          )}

          {/* Search and Filters */}
          <div className="bg-white rounded-xl shadow-2xl p-6 mb-6 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label htmlFor="search" className="block text-sm font-semibold text-gray-700 mb-2">
                  Search Projects
                </label>
                <input
                  type="text"
                  id="search"
                  placeholder="Project number or name..."
                  value={searchTerm}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label htmlFor="statusFilter" className="block text-sm font-semibold text-gray-700 mb-2">
                  Project Status
                </label>
                <div className="relative filter-dropdown-container">
                  <button
                    type="button"
                    onClick={() => setStatusFilterDropdownOpen(!statusFilterDropdownOpen)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent bg-white text-left font-medium text-gray-900"
                  >
                    {statusFilter === 'all' ? 'All Projects' : 
                     statusFilter === 'active' ? 'Active Projects' : 
                     statusFilter === 'completed' ? 'Completed Projects' : 
                     statusFilter === 'on-hold' ? 'On Hold Projects' : 
                     statusFilter === 'cancelled' ? 'Cancelled Projects' : 
                     statusFilter === 'inactive' ? 'Inactive Projects' : 'All Projects'}
                    <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {statusFilterDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      {[
                        { value: 'all', label: 'All Projects' },
                        { value: 'active', label: 'Active Projects' },
                        { value: 'completed', label: 'Completed Projects' },
                        { value: 'on-hold', label: 'On Hold Projects' },
                        { value: 'cancelled', label: 'Cancelled Projects' },
                        { value: 'inactive', label: 'Inactive Projects' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            handleFilterChange('status', option.value);
                            setStatusFilterDropdownOpen(false);
                          }}
                          className={`w-full px-3 py-2 text-left font-medium hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                            statusFilter === option.value ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Date Filter */}
              <div>
                <label htmlFor="dateFilter" className="block text-sm font-semibold text-gray-700 mb-2">
                  Date Created
                </label>
                <div className="relative filter-dropdown-container">
                  <button
                    type="button"
                    onClick={() => setDateFilterDropdownOpen(!dateFilterDropdownOpen)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent bg-white text-left font-medium text-gray-900"
                  >
                    {dateFilter === 'all' ? 'All Time' : 
                     dateFilter === 'recent' ? 'Last 30 Days' : 
                     dateFilter === 'older' ? 'Older than 90 Days' : 'All Time'}
                    <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {dateFilterDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      {[
                        { value: 'all', label: 'All Time' },
                        { value: 'recent', label: 'Last 30 Days' },
                        { value: 'older', label: 'Older than 90 Days' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            handleFilterChange('date', option.value);
                            setDateFilterDropdownOpen(false);
                          }}
                          className={`w-full px-3 py-2 text-left font-medium hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                            dateFilter === option.value ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Results Count */}
              <div className="flex items-end">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">{Array.isArray(filteredProjects) ? filteredProjects.length : 0}</span> of {Array.isArray(projects) ? projects.length : 0} projects
                </div>
              </div>
            </div>
          </div>

          {/* Projects List */}
          <div className="bg-white rounded-xl shadow-2xl p-8 border border-gray-200">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900">
                Projects ({Array.isArray(filteredProjects) ? filteredProjects.length : 0})
              </h2>
            </div>
            
            {!Array.isArray(projects) || projects.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">No projects yet</h3>
                <p className="text-gray-600 mb-6 text-lg">Get started by creating your first project.</p>
                <button
                  onClick={() => {
                    setShowForm(true);
                    // Scroll to top when opening the create form
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors mx-auto"
                >
                  <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Create Project</span>
                </button>
              </div>
            ) : !Array.isArray(filteredProjects) || filteredProjects.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">No projects found</h3>
                <p className="text-gray-600 mb-6 text-lg">Try adjusting your search criteria or filters.</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDateFilter('all');
                    setCurrentPage(1);
                  }}
                  className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                  style={{ color: '#7F1D1D' }}
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('project_name')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Project</span>
                          {sortConfig.key === 'project_name' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contract Value</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Budget</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">External IDs</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getCurrentProjects().map((project) => (
                      <tr 
                        key={project.vuid} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/projects/${project.vuid}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{project.project_name}</div>
                            <div className="text-sm text-gray-500">{project.project_number}</div>
                            {project.description && (
                              <div className="text-xs text-gray-600 truncate max-w-xs" data-tooltip={project.description}>
                                {project.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            project.status === 'active' ? 'bg-green-100 text-green-800' :
                            project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            project.status === 'on-hold' ? 'bg-yellow-100 text-yellow-800' :
                            project.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                            project.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {project.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1) : 'Active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          ${(project.total_contract_value || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          ${(project.current_budget || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {externalIdsMap[project.vuid] && externalIdsMap[project.vuid].length > 0 ? (
                            <div className="flex flex-col space-y-1">
                              {externalIdsMap[project.vuid].map((extId, idx) => (
                                <div key={idx} className="flex items-center space-x-2">
                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                    <span className="font-semibold mr-1">{extId.integration_name}:</span>
                                    <span className="font-mono">{extId.external_id}</span>
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleIntegrationModal(project);
                              }}
                              className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                              data-tooltip="Send to integration"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(project);
                              }}
                              className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                              data-tooltip="Edit project"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(project.vuid);
                              }}
                              className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                              data-tooltip="Delete project"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {Array.isArray(filteredProjects) && filteredProjects.length > projectsPerPage && (
              <div className="mt-8">
                {/* Page Info */}
                <div className="text-center mb-4 text-sm text-gray-600">
                  Showing {((currentPage - 1) * projectsPerPage) + 1} to {Math.min(currentPage * projectsPerPage, Array.isArray(filteredProjects) ? filteredProjects.length : 0)} of {Array.isArray(filteredProjects) ? filteredProjects.length : 0} projects
                </div>
                
                <div className="flex items-center justify-center">
                  <nav className="flex items-center space-x-2">
                    {/* Previous Page */}
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    {Array.from({ length: Math.ceil((Array.isArray(filteredProjects) ? filteredProjects.length : 0) / projectsPerPage) }, (_, i) => i + 1).map((number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === number
                            ? 'bg-gray-800 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {number}
                      </button>
                    ))}

                    {/* Next Page */}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === Math.ceil((Array.isArray(filteredProjects) ? filteredProjects.length : 0) / projectsPerPage)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === Math.ceil((Array.isArray(filteredProjects) ? filteredProjects.length : 0) / projectsPerPage)
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create/Edit Project Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingProject ? 'Edit Project' : 'Create New Project'}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="project_number" className="block text-sm font-semibold text-gray-700 mb-2">
                      Project Number *
                    </label>
                    <input
                      type="text"
                      id="project_number"
                      value={formData.project_number}
                      onChange={(e) => setFormData({...formData, project_number: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 ${
                        errors.project_number ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter project number"
                    />
                    {errors.project_number && (
                      <p className="mt-1 text-sm text-red-600">{errors.project_number}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="project_name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Project Name *
                    </label>
                    <input
                      type="text"
                      id="project_name"
                      value={formData.project_name}
                      onChange={(e) => setFormData({...formData, project_name: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 ${
                        errors.project_name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter project name"
                    />
                    {errors.project_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.project_name}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
                    placeholder="Enter project description"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="start_date" className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
                    />
                  </div>

                  <div>
                    <label htmlFor="end_date" className="block text-sm font-semibold text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="end_date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
                    />
                  </div>

                  <div>
                    <label htmlFor="status" className="block text-sm font-semibold text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
                    >
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="on-hold">On Hold</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Project Settings Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Settings</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="allow_project_cost_codes"
                        checked={formData.allow_project_cost_codes}
                        onChange={(e) => setFormData({...formData, allow_project_cost_codes: e.target.checked})}
                        className="h-4 w-4 text-gray-800 focus:ring-gray-800 border-gray-300 rounded"
                      />
                      <label htmlFor="allow_project_cost_codes" className="ml-2 block text-sm text-gray-700">
                        Allow Project Cost Codes
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 ml-6">Enable custom cost codes specific to this project</p>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="allocate_contract_lines_to_cost_codes"
                        checked={formData.allocate_contract_lines_to_cost_codes}
                        onChange={(e) => setFormData({...formData, allocate_contract_lines_to_cost_codes: e.target.checked})}
                        className="h-4 w-4 text-gray-800 focus:ring-gray-800 border-gray-300 rounded"
                      />
                      <label htmlFor="allocate_contract_lines_to_cost_codes" className="ml-2 block text-sm text-gray-700">
                        Allocate Contract Lines to Cost Codes
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 ml-6">Automatically allocate contract line items to cost codes when creating billings</p>

                    <div>
                      <label htmlFor="labor_cost_method" className="block text-sm font-semibold text-gray-700 mb-2">
                        Labor Cost Method
                      </label>
                      <select
                        id="labor_cost_method"
                        value={formData.labor_cost_method}
                        onChange={(e) => setFormData({...formData, labor_cost_method: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
                      >
                        <option value="default">Default</option>
                        <option value="hourly">Hourly</option>
                        <option value="salary">Salary</option>
                        <option value="contract">Contract</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Method for calculating labor costs for this project</p>
                    </div>
                  </div>
                </div>

                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-600 text-sm">{errors.submit}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-6 rounded-lg transition-colors"
                    style={{ color: '#7F1D1D' }}
                  >
                    {editingProject ? 'Update Project' : 'Create Project'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Retrieve Projects Modal */}
        {showRetrieveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Retrieve Projects from Integration</h2>
                <button
                  onClick={() => setShowRetrieveModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-600">
                  Select a Procore integration to retrieve projects from.
                </p>
                
                {integrations.length === 0 ? (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-800 text-sm">
                      No active Procore integrations found. Please configure a Procore integration first.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {integrations.map((integration) => (
                      <button
                        key={integration.vuid}
                        onClick={async () => {
                          setRetrieveIntegration(integration.integration_name || 'Procore');
                          setShowRetrieveModal(false);
                          await fetchProjectsFromIntegration(integration.vuid);
                          setShowRetrievedProjectsModal(true);
                        }}
                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                            <span className="text-orange-600 font-bold">P</span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{integration.integration_name}</h3>
                            <p className="text-sm text-gray-500">
                              {integration.base_url || 'Procore'} - {integration.company_id ? `Company ID: ${integration.company_id}` : 'No Company ID configured'}
                            </p>
                            {!integration.company_id && (
                              <p className="text-xs text-red-600 mt-1">⚠️ Company ID required for this integration</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Retrieved Projects Modal */}
        {showRetrievedProjectsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Retrieved Projects from {retrieveIntegration?.charAt(0).toUpperCase() + retrieveIntegration?.slice(1)}
                </h2>
                <button
                  onClick={() => {
                    setShowRetrievedProjectsModal(false);
                    setRetrieveIntegration(null);
                    setRetrievedProjectsData([]);
                    setSelectedProjectsToImport([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-600">
                  Select the projects you want to import into Vermillion.
                </p>
                
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center space-x-4">
                      <input
                        type="checkbox"
                        checked={selectedProjectsToImport.length === retrievedProjectsData.length && retrievedProjectsData.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProjectsToImport(retrievedProjectsData.map(p => p.id));
                          } else {
                            setSelectedProjectsToImport([]);
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="font-medium text-gray-700">Select All</span>
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {retrievedProjectsData.map((project) => (
                      <div key={project.id} className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50">
                        <div className="flex items-center space-x-4">
                          <input
                            type="checkbox"
                            checked={selectedProjectsToImport.includes(project.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedProjectsToImport(prev => [...prev, project.id]);
                              } else {
                                setSelectedProjectsToImport(prev => prev.filter(id => id !== project.id));
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{project.name || project.project_name}</h3>
                            <p className="text-sm text-gray-500">{project.description || 'No description'}</p>
                            <div className="flex items-center space-x-4 mt-1 flex-wrap">
                              <span className="text-xs text-gray-400">Number: {getExternalProjectNumber(project) || '—'}</span>
                              <span className="text-xs text-gray-400">Status: {project.status}</span>
                              <span className="text-xs text-gray-400">Start: {project.start_date}</span>
                              <span className="text-xs text-gray-400">End: {project.end_date}</span>
                              {project.project_manager && (
                                <span className="text-xs text-gray-400">PM: {project.project_manager}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setShowRetrievedProjectsModal(false);
                      setRetrieveIntegration(null);
                      setRetrieveIntegrationVuid(null);
                      setRetrievedProjectsData([]);
                      setSelectedProjectsToImport([]);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                        // Import selected projects
                      try {
                        const projectsToImport = retrievedProjectsData.filter(project => 
                          selectedProjectsToImport.includes(project.id)
                        );
                        
                        let successCount = 0;
                        let errorMessages = [];
                        
                        for (const project of projectsToImport) {
                          try {
                            const projectData = {
                              project_number: getExternalProjectNumber(project) || `IMPORT-${project.id}`,
                              project_name: project.name,
                              description: project.description || `Imported from ${retrieveIntegration}`,
                              start_date: project.start_date,
                              end_date: project.end_date,
                              status: mapExternalProjectStatus(project.status)
                            };
                            
                            // Create the project
                            const createResponse = await axios.post(`${API_BASE_URL}/api/projects`, projectData);
                            const newProject = createResponse.data?.project || createResponse.data;
                            
                            // Save the external system ID mapping if we have an integration VUID and Procore project ID
                            if (retrieveIntegrationVuid && project.id && newProject?.vuid) {
                              try {
                                await axios.post(`${API_BASE_URL}/api/external-system-ids`, {
                                  integration_vuid: retrieveIntegrationVuid,
                                  object_type: 'project',
                                  project_vuid: newProject.vuid,
                                  object_vuid: newProject.vuid,
                                  external_id: String(project.id), // Procore project ID
                                  external_object_type: 'project',
                                  external_metadata: {
                                    name: project.name,
                                    project_number: project.project_number,
                                    number: project.number,
                                    display_name: project.display_name,
                                    company: project.company
                                  },
                                  external_status: project.status || 'active'
                                });
                                console.log(`Saved external system ID mapping for project ${newProject.project_name || newProject.vuid} (Procore ID: ${project.id})`);
                              } catch (error) {
                                console.error('Error saving external system ID mapping:', error);
                                // Don't fail the import if mapping fails
                              }
                            }
                            
                            successCount++;
                          } catch (error) {
                            const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
                            errorMessages.push(`${project.name || project.id}: ${errorMsg}`);
                            console.error(`Error importing project ${project.name || project.id}:`, error);
                          }
                        }
                        
                        setShowRetrievedProjectsModal(false);
                        setRetrieveIntegration(null);
                        setRetrieveIntegrationVuid(null);
                        setRetrievedProjectsData([]);
                        setSelectedProjectsToImport([]);
                        
                        if (errorMessages.length > 0 && successCount > 0) {
                          setSuccessMessage(`${successCount} project(s) imported successfully. Some errors occurred.`);
                          alert(`Imported ${successCount} project(s) successfully.\n\nErrors:\n${errorMessages.join('\n')}`);
                        } else if (errorMessages.length > 0) {
                          setSuccessMessage('Error importing projects. Please try again.');
                          alert(`Error importing projects:\n${errorMessages.join('\n')}`);
                        } else if (successCount > 0) {
                          setSuccessMessage(`${successCount} project(s) imported successfully!`);
                        }
                        
                        // Refresh the projects list and external IDs
                        fetchProjects();
                        fetchExternalIds();
                      } catch (error) {
                        console.error('Error importing projects:', error);
                        const errorMsg = error.response?.data?.error || error.message || 'Error importing projects. Please try again.';
                        setSuccessMessage(`Error: ${errorMsg}`);
                        alert(`Error importing projects: ${errorMsg}`);
                      }
                    }}
                    disabled={selectedProjectsToImport.length === 0}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                  >
                    Import {selectedProjectsToImport.length} Projects
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Project to Integration Modal */}
        {showIntegrationModal && selectedProject && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Send Project to Integration</h2>
                <button
                  onClick={() => {
                    setShowIntegrationModal(false);
                    setSelectedProject(null);
                    setSelectedIntegration(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Project Details</h3>
                  <p className="text-sm text-gray-700"><strong>Name:</strong> {selectedProject.project_name}</p>
                  <p className="text-sm text-gray-700"><strong>Number:</strong> {selectedProject.project_number}</p>
                  {selectedProject.description && (
                    <p className="text-sm text-gray-700"><strong>Description:</strong> {selectedProject.description}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Integration
                  </label>
                  {integrations.length === 0 ? (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-yellow-800 text-sm">
                        No active integrations found that support projects. Please configure an integration first.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {integrations.map((integration) => (
                        <button
                          key={integration.vuid}
                          onClick={() => setSelectedIntegration(integration)}
                          className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                            selectedIntegration?.vuid === integration.vuid
                              ? 'border-gray-800 bg-gray-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                              <span className="text-orange-600 font-bold">P</span>
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900">{integration.integration_name}</h3>
                              <p className="text-sm text-gray-500">
                                {integration.base_url || 'Procore'} - {integration.company_id ? `Company ID: ${integration.company_id}` : 'No Company ID'}
                              </p>
                            </div>
                            {selectedIntegration?.vuid === integration.vuid && (
                              <svg className="w-5 h-5 text-gray-800" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    onClick={() => {
                      setShowIntegrationModal(false);
                      setSelectedProject(null);
                      setSelectedIntegration(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendProjectToIntegration}
                    disabled={!selectedIntegration}
                    className={`px-6 py-3 font-semibold rounded-lg transition-all ${
                      selectedIntegration
                        ? 'bg-gray-800 text-white hover:bg-gray-900'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Send to Integration
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
